import { createFileRoute } from "@tanstack/react-router";
import { PageSection } from "@/components/site/PageSection";
import { EsPageShell } from "@/components/site/EsPageShell";
import { EsContactForm } from "@/components/site/EsContactForm";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { faqPageJson } from "@/lib/faq-schema";

const FAQ_URL = `${SITE}/es/preguntas-frecuentes`;

interface EsFaq {
  id: string;
  q: string;
  a: string;
}

interface EsFaqGroup {
  label: string;
  items: EsFaq[];
}

const FAQ_GROUPS: EsFaqGroup[] = [
  {
    label: "Cirugía bariátrica",
    items: [
      {
        id: "que-es-cirugia-bariatrica",
        q: "¿Qué es la cirugía bariátrica?",
        a: "La cirugía bariátrica es un grupo de procedimientos quirúrgicos que ayudan a las personas a bajar de peso mediante cambios en el sistema digestivo. Según el procedimiento, la cirugía puede reducir el tamaño del estómago, modificar el recorrido del tubo digestivo, o ambas cosas. Por lo general se recomienda cuando la dieta, el ejercicio y los medicamentos no han producido resultados suficientes en pacientes con problemas de salud relacionados con la obesidad.",
      },
      {
        id: "tipos-de-procedimientos",
        q: "¿Qué tipos de procedimientos bariátricos ofrecen?",
        a: "Los procedimientos con respaldo científico que ofrecemos incluyen: el bypass gástrico Roux-en-Y (el estómago se divide en una bolsa pequeña que se conecta directamente al intestino delgado), la manga gástrica (se extirpa aproximadamente el 75–80% del estómago), la banda gástrica ajustable (Lap-Band), el balón intragástrico Orbera®, la reducción endoscópica de la salida gástrica (TORe) y la cirugía bariátrica de revisión para pacientes con complicaciones o pérdida de peso insuficiente después de un procedimiento anterior.",
      },
      {
        id: "cual-procedimiento-es-para-mi",
        q: "¿Cuál procedimiento es el adecuado para mí?",
        a: "El procedimiento adecuado depende de su IMC, sus condiciones de salud, sus hábitos alimentarios, sus cirugías previas y sus metas personales. Nuestro equipo revisará su historial médico completo y le ayudará a elegir la mejor opción. No hay una respuesta única para todos.",
      },
      {
        id: "quien-califica",
        q: "¿Quién califica para la cirugía bariátrica?",
        a: "Los criterios actuales de los NIH incluyen: un IMC de 35 o más, o un IMC de 30 a 34.9 con al menos una condición relacionada con la obesidad (por ejemplo, diabetes tipo 2, apnea del sueño, hipertensión o enfermedad cardíaca), antecedentes de intentos fallidos de pérdida de peso sin cirugía, y el compromiso de mantener cambios de estilo de vida a largo plazo.",
      },
    ],
  },
  {
    label: "El procedimiento",
    items: [
      {
        id: "laparoscopica",
        q: "¿La cirugía bariátrica se realiza por laparoscopia?",
        a: "Sí, en prácticamente todos los casos. Utilizamos técnicas laparoscópicas mínimamente invasivas, que implican incisiones pequeñas, una cámara e instrumentos especializados. Esto significa menos dolor, cicatrices más pequeñas y una recuperación más rápida en comparación con la cirugía abierta.",
      },
      {
        id: "duracion-cirugia",
        q: "¿Cuánto dura la cirugía?",
        a: "El Dr. Fitzer realiza la mayoría de los procedimientos en una hora o menos. La cirugía de revisión toma un poco más de tiempo. Factores individuales (como varias operaciones previas u obesidad muy extrema) pueden afectar el tiempo operatorio.",
      },
      {
        id: "estancia-hospitalaria",
        q: "¿Cuánto tiempo estaré en el hospital?",
        a: "La mayoría de los pacientes pasan una noche en el hospital después de la manga gástrica o el bypass gástrico. Los pacientes de banda gástrica, TORe y ESG generalmente se van a casa el mismo día. Las fechas de alta a veces varían según la evaluación del Dr. Fitzer.",
      },
    ],
  },
  {
    label: "Seguro médico",
    items: [
      {
        id: "seguro-cubre-cirugia",
        q: "¿Mi seguro médico cubre la cirugía bariátrica?",
        a: "Si tiene un seguro del gobierno (Medicare, Medicaid, Tricare, etc.), felicidades: sí tiene cobertura para la cirugía bariátrica. Si tiene un seguro privado (por ejemplo, Cigna, United, Aetna, BCBS), es posible que la tenga. Todas las aseguradoras privadas ofrecen cobertura de cirugía bariátrica, pero lamentablemente no todas las pólizas la incluyen. Llámenos: podemos ayudarle a averiguar si su póliza incluye la cobertura.",
      },
      {
        id: "requisitos-con-seguro",
        q: "¿Los requisitos son diferentes si uso mi seguro?",
        a: "Curiosamente, sí. Las aseguradoras se apegan a las antiguas guías de los NIH para la cirugía de pérdida de peso: un IMC mayor de 40 sin enfermedades relacionadas, o mayor de 35 con ellas. Esas guías tienen más de treinta años (se publicaron en 1991) y existe un amplio consenso médico de que son demasiado restrictivas, siendo las aseguradoras la excepción más notable a ese consenso.",
      },
    ],
  },
  {
    label: "Recuperación",
    items: [
      {
        id: "cuanto-dolor",
        q: "¿Cuánto dolor debo esperar?",
        a: "Por lo general no es demasiado. Es común sentir una molestia leve a moderada durante el primer día o dos después de la cirugía laparoscópica, y suele desaparecer hacia el segundo día. La mayoría de los pacientes se manejan bien con analgésicos orales.",
      },
      {
        id: "que-podre-comer",
        q: "¿Qué podré comer después de la cirugía?",
        a: "A largo plazo, preferimos que los pacientes coman comidas pequeñas y equilibradas, ricas en proteína: por lo general tres comidas pequeñas al día. Se desaconsejan los alimentos con alto contenido de azúcar y grasa, que pueden causar el «síndrome de dumping» en los pacientes de bypass. Recomendamos evitar alimentos y bebidas con edulcorantes, ya sea azúcar o endulzantes sin calorías.",
      },
      {
        id: "vitaminas-para-siempre",
        q: "¿Tendré que tomar vitaminas para siempre?",
        a: "Sí, aunque necesitaría las mismas vitaminas si bajara una cantidad similar de peso solo con dieta. Virginia Bariatric le entregará por escrito un régimen específico con suficiente anticipación a la cirugía.",
      },
      {
        id: "sindrome-dumping",
        q: "¿Qué es el síndrome de dumping?",
        a: "Es el nombre que damos a los síntomas desagradables que pueden aparecer después de un bypass o una manga gástrica como respuesta a alimentos muy ricos en azúcar refinada o grasa líquida. Se produce por una liberación anormal de hormonas intestinales cuando los alimentos pasan rápidamente al intestino delgado. Los síntomas incluyen náuseas, cólicos, sudoración, mareo y a veces diarrea. Por lo general se puede evitar siguiendo las pautas de alimentación.",
      },
    ],
  },
  {
    label: "Manga gástrica",
    items: [
      {
        id: "manga-o-bypass",
        q: "¿Manga o bypass: cuál es mejor?",
        a: "Ambas son operaciones excelentes, con pérdida de peso y remisión de diabetes similares. La manga es más sencilla y tiene menos complicaciones; el bypass suele preferirse en pacientes con reflujo ácido importante o ciertas condiciones metabólicas. La mejor elección es individual: vea nuestra comparación entre bypass y manga.",
      },
      {
        id: "manga-reversible",
        q: "¿La manga gástrica se puede revertir?",
        a: "No. Como se extirpa permanentemente una parte del estómago, la manga no es reversible, aunque en el futuro se puede convertir a otro procedimiento si es necesario.",
      },
      {
        id: "recuperacion-manga",
        q: "¿Cuánto dura la recuperación después de la manga gástrica?",
        a: "La mayoría de los pacientes pasan una noche en el hospital y vuelven a sus actividades normales en unas cuatro semanas. Usted comenzará con una dieta líquida de inmediato y avanzará a alimentos en puré, blandos y normales durante las ocho semanas siguientes.",
      },
      {
        id: "seguro-cubre-manga",
        q: "¿El seguro cubre la manga gástrica?",
        a: "Muchas aseguradoras importantes cubren la manga gástrica cuando se cumplen los criterios médicos, como un IMC de 35 o más, o de 30 o más con una condición relacionada con la obesidad. Nuestro equipo verifica los beneficios y le acompaña durante el proceso de autorización.",
      },
    ],
  },
  {
    label: "Medicamentos para bajar de peso",
    items: [
      {
        id: "que-medicamentos-receta",
        q: "¿Qué medicamentos receta el Dr. Fitzer?",
        a: "Toda la gama de opciones aprobadas por la FDA: semaglutida (Wegovy, Ozempic), tirzepatida (Zepbound, Mounjaro) y medicamentos orales como fentermina, Contrave, orlistat y dietilpropión. La elección depende de su historial de salud, sus metas, su seguro y su tolerancia.",
      },
      {
        id: "cuanto-peso-con-glp1",
        q: "¿Cuánto peso puedo esperar perder con un medicamento GLP-1?",
        a: "En los grandes estudios clínicos, la pérdida promedio de peso corporal total fue de aproximadamente 15% con semaglutida y cerca de 20% con tirzepatida en 68 a 72 semanas, junto con cambios de alimentación y actividad. Los resultados individuales varían mucho.",
      },
      {
        id: "efectos-secundarios-glp1",
        q: "¿Cuáles son los efectos secundarios más comunes de los medicamentos GLP-1?",
        a: "Los más comunes son náuseas, diarrea, estreñimiento y vómitos. Suelen ser leves a moderados, aparecen durante los aumentos de dosis y con frecuencia mejoran con el tiempo y con un aumento de dosis más lento.",
      },
      {
        id: "seguro-cubre-medicamentos",
        q: "¿Mi seguro cubrirá Zepbound o Wegovy en Virginia?",
        a: "Depende de dos filtros distintos. Primero: ¿su plan cubre en absoluto los medicamentos para bajar de peso? Muchos planes de empleadores de Virginia, y todos los planes individuales y de grupos pequeños del mercado, los excluyen por completo. Segundo: si la categoría está cubierta, ¿cumple usted los criterios clínicos de autorización previa del plan? Por lo general, un IMC de 30 o más, o de 27 o más con una condición relacionada con el peso, además de esfuerzos documentados de cambio de estilo de vida. Pregunte primero a su plan por la exclusión, porque ninguna documentación puede superar una exclusión del beneficio.",
      },
    ],
  },
  {
    label: "Cirugía de revisión",
    items: [
      {
        id: "revision-fracaso",
        q: "¿Necesitar una revisión significa que mi primera cirugía fracasó, o que yo fracasé?",
        a: "Ninguna de las dos cosas. Existen varias condiciones adquiridas que pueden hacer que un procedimiento bariátrico que antes funcionaba bien pierda eficacia: por ejemplo, la bolsa de un bypass puede reconectarse con el resto del estómago (fístula gastrogástrica), y el embarazo a veces causa una dilatación de la bolsa o la manga que provoca una recuperación importante de peso. Estos problemas se pueden corregir. En cualquier caso, la obesidad es una enfermedad crónica que se resiste al tratamiento. La necesidad de una revisión no es rara; es de esperar en un grupo de pacientes y no debe tomarse como un indicio de fracaso personal. Es cuestión de biología y anatomía.",
      },
      {
        id: "revision-reflujo",
        q: "¿Una revisión ayudará con mi reflujo ácido?",
        a: "Claro que puede. El reflujo severo después de una manga gástrica es una de las razones mejor establecidas para una revisión, y la conversión a un bypass gástrico Roux-en-Y es muy eficaz para resolverlo: de hecho, suele apagar el reflujo como un interruptor, con el beneficio adicional de producir más pérdida de peso en la mayoría de las personas.",
      },
    ],
  },
];

const ALL_FAQS: EsFaq[] = FAQ_GROUPS.flatMap((g) => g.items);

export const Route = createFileRoute("/es/preguntas-frecuentes")({
  head: () => {
    const base = esHead("faq");
    return {
      ...base,
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(esWebPageSchema("faq")),
            },
            {
              type: "application/ld+json",
              children: faqPageJson({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "@id": `${FAQ_URL}#faq`,
                url: FAQ_URL,
                inLanguage: "es-US",
                isPartOf: { "@id": `${FAQ_URL}#webpage` },
                mainEntityOfPage: { "@id": `${FAQ_URL}#webpage` },
                publisher: { "@id": `${SITE}/#organization` },
                mainEntity: ALL_FAQS.map((it) => ({
                  "@type": "Question",
                  "@id": `${FAQ_URL}#${it.id}`,
                  url: `${FAQ_URL}#${it.id}`,
                  name: it.q,
                  inLanguage: "es-US",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: it.a,
                    url: `${FAQ_URL}#${it.id}`,
                  },
                })),
              }),
            },
          ]
        : [],
    };
  },
  component: EsFaqPage,
});

function EsFaqPage() {
  return (
    <EsPageShell
      page="faq"
      title="Preguntas Frecuentes"
      intro="Respuestas a algunas de las preguntas más comunes que escuchamos de nuestros pacientes. ¿No encuentra su pregunta? Escríbanos con el formulario al final de la página."
    >
      <PageSection className="pb-6">
        <nav
          aria-label="Enlaces rápidos de preguntas frecuentes"
          className="mx-auto max-w-4xl rounded-2xl bg-brand-soft/50 p-3 md:p-4"
        >
          {FAQ_GROUPS.map((group) => (
            <div key={group.label} className="space-y-0.5">
              <p className="text-xs font-medium uppercase tracking-[0.1em] text-foreground/60">
                {group.label}
              </p>
              <ul className="space-y-0 text-foreground/90">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="underline-offset-2 transition-colors hover:text-brand hover:underline"
                    >
                      {item.q}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </PageSection>

      {FAQ_GROUPS.map((group) => (
        <PageSection key={group.label} className="pb-6">
          <h2 className="text-center font-serif text-2xl text-brand-deep">
            {group.label}
          </h2>
          <div className="mx-auto mt-4 max-w-4xl divide-y divide-border rounded-2xl border border-border bg-card">
            {group.items.map((item) => (
              <article key={item.id} id={item.id} className="scroll-mt-24 p-4 md:p-5">
                <h3 className="text-lg font-medium text-foreground">{item.q}</h3>
                <p className="mt-2 text-[0.95rem] leading-relaxed text-foreground/80">
                  {item.a}
                </p>
              </article>
            ))}
          </div>
        </PageSection>
      ))}

      <PageSection className="pb-6">
        <h2 className="text-center font-serif text-2xl text-brand-deep">
          ¿Tiene otra pregunta?
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-foreground/80">
          Envíenos su pregunta o solicite una cita y el consultorio del
          Dr. Fitzer se comunicará con usted. También puede llamarnos al{" "}
          <a className="font-medium text-brand-logo" href="tel:7037099771">
            (703) 709-9771
          </a>
          .
        </p>
        <div className="mx-auto mt-4 max-w-2xl">
          <EsContactForm idPrefix="es-faq-contact" />
        </div>
      </PageSection>
    </EsPageShell>
  );
}
